import { rgb } from "pdf-lib";
import type { PDFFont, PDFPage, PDFImage } from "pdf-lib";
import { buildPaymentSummaryRows, type SummaryRow } from "./summaryRows";

// =====================================================
// SEQ-145 PAGE 1 - PAYMENT CLAIM SUMMARY (ported from the Send Report edge
// function at supabase/functions/generate-progress-claim/index.ts:310-585).
// Kept coordinate-for-coordinate identical so an edited report regenerates the
// same page 1 the edge function first produced. formatCurrency is ported too
// (the edge version: dash for near-zero, AUD currency style) - deliberately NOT
// the file's `fmt`, which omits the currency symbol.
//
// Split out of regenerateClaimSummaryPdf.ts (Sep 2026 relayout hotfix) so this
// pure drawing function has no dependency on the browser-only Supabase client
// that file imports - scripts/renderClaimSummaryPreview.ts renders through
// this module directly under Deno.
// =====================================================
const formatCurrencyPage1 = (amount: number): string => {
  // Check specifically for 0 or -0 to display dash
  if (Math.abs(amount) < 0.01) return "-";
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
  }).format(amount);
};

export interface Page1Data {
  companyName: string;
  companyAddress: string;
  companyCity: string;
  companyABN: string;
  companyPhone: string;
  companyEmail: string;
  builderName: string;
  claimNumber: number | string;
  projectName: string;
  siteAddress: string;
  contractDescription: string;
  claimDateStr: string;
  paymentDueStr: string;
  paymentPeriodDays: number | null;
  originalContractValue: number;
  approvedVariations: number;
  revisedContractValue: number;
  A: number;
  B: number;
  /**
   * Amendment 2 deduction split. Present only for reports frozen with the new
   * summary shape; when absent the combined B row is drawn (old new-format
   * reports stay reproducible, never derived from the frozen B).
   */
  bReceipts?: number;
  retentionHeldToDate?: number;
  C: number;
  P: number;
  installHire: number;
  V: number;
  R: number;
  release: number;
  G: number;
  T: number;
  /**
   * Josh (27 Jul, additive): true retention held on every PRIOR claim (all
   * claims, not paid-only) + the 5% ceiling. Present only on reports frozen
   * after the fix; when absent the "held to date vs ceiling" row is omitted so
   * older reports regenerate unchanged.
   */
  accumulatedRetention?: number;
  retentionCeiling?: number;
  retentionCeilingPercent?: number;
  bankName: string;
  bsb: string;
  account: string;
  accountName: string;
}

type Color = ReturnType<typeof rgb>;

export function drawPaymentClaimPage1(
  page: PDFPage,
  font: PDFFont,
  fontBold: PDFFont,
  logoImage: PDFImage,
  data: Page1Data,
): void {
  const { width, height } = page.getSize();

  // Brand palette (sampled from src/index.css): navy #00102e, blue #1e73be.
  const NAVY = rgb(0, 0.063, 0.18);
  const LIGHT = rgb(0.9, 0.93, 0.97);
  const LIGHT_ALT = rgb(0.98, 0.985, 0.995);
  const BORDER = rgb(0.8, 0.84, 0.89);
  const TEXT = rgb(0.1, 0.12, 0.16);
  const MUTED = rgb(0.42, 0.46, 0.52);
  const WHITE = rgb(1, 1, 1);

  const text = (
    t: string,
    x: number,
    y: number,
    size = 10,
    opts: { font?: PDFFont; color?: Color; align?: "left" | "right" | "center" } = {},
  ) => {
    const f = opts.font || font;
    const w = f.widthOfTextAtSize(t, size);
    let xp = x;
    if (opts.align === "right") xp = x - w;
    else if (opts.align === "center") xp = x - w / 2;
    page.drawText(t, { x: xp, y: height - y, size, font: f, color: opts.color || TEXT });
  };
  const line = (
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    th = 1,
    color: Color = BORDER,
  ) => {
    page.drawLine({ start: { x: x1, y: height - y1 }, end: { x: x2, y: height - y2 }, thickness: th, color });
  };
  const rect = (
    x: number,
    yTop: number,
    w: number,
    h: number,
    color?: Color,
    opts: { borderColor?: Color; borderWidth?: number } = {},
  ) => {
    page.drawRectangle({
      x,
      y: height - yTop - h,
      width: w,
      height: h,
      ...(color ? { color } : {}),
      ...(opts.borderColor ? { borderColor: opts.borderColor, borderWidth: opts.borderWidth ?? 1 } : {}),
    });
  };
  const fit = (t: string, maxW: number, size: number): string => {
    let s = t || "-";
    if (font.widthOfTextAtSize(s, size) <= maxW) return s;
    while (s.length > 1 && font.widthOfTextAtSize(`${s}...`, size) > maxW) {
      s = s.slice(0, -1);
    }
    return `${s}...`;
  };

  const margin = 50;
  const right = width - margin;
  const contentW = width - margin * 2;

  // --- Header: logo left, company block right ---
  const logoW = 170;
  const logoH = logoImage.height * (logoW / logoImage.width);
  page.drawImage(logoImage, {
    x: margin,
    y: height - 36 - logoH,
    width: logoW,
    height: logoH,
  });

  let hy = 42;
  text(data.companyName, right, hy, 12, { font: fontBold, align: "right", color: NAVY });
  hy += 14;
  text(data.companyAddress, right, hy, 9, { align: "right", color: MUTED });
  hy += 11;
  text(data.companyCity, right, hy, 9, { align: "right", color: MUTED });
  hy += 11;
  text(`Phone: ${data.companyPhone}`, right, hy, 9, { align: "right", color: MUTED });
  hy += 11;
  text(`Email: ${data.companyEmail}`, right, hy, 9, { align: "right", color: MUTED });
  hy += 11;
  text(`ABN: ${data.companyABN}`, right, hy, 9, { align: "right", color: MUTED });

  let y = Math.max(36 + logoH, hy) + 14;

  // Header rule under the company block. TAX INVOICE title removed (Josh, 24 Jul:
  // "Remove Tax invoice, this is not needed"). Mirrors the edge function's page 1.
  line(margin, y, right, y, 0.75, NAVY);
  y += 16;

  // --- Details grid (2x4). Left column widened to 60% (Josh, 24 Jul) so long
  // builder names / site addresses fit before truncating. ---
  const gridRowH = 30;
  const gridH = gridRowH * 4;
  const leftW = contentW * 0.6;
  const rightW = contentW - leftW;
  rect(margin, y, contentW, gridH, undefined, { borderColor: BORDER, borderWidth: 1 });
  line(margin + leftW, y, margin + leftW, y + gridH, 0.75, BORDER);
  const leftFields: [string, string][] = [
    ["To", data.builderName],
    ["Project", data.projectName],
    ["Site", data.siteAddress],
    ["Contract", data.contractDescription],
  ];
  const rightFields: [string, string][] = [
    ["Claim No.", String(data.claimNumber)],
    ["Claim Date", data.claimDateStr],
    ["Payment Due", data.paymentDueStr],
    [
      "Payment Period",
      data.paymentPeriodDays != null ? `${data.paymentPeriodDays} days` : "-",
    ],
  ];
  for (let i = 0; i < 4; i++) {
    const ry = y + i * gridRowH;
    if (i > 0) line(margin, ry, margin + contentW, ry, 0.4, rgb(0.9, 0.92, 0.94));
    text(leftFields[i][0].toUpperCase(), margin + 10, ry + 12, 7, { font: fontBold, color: MUTED });
    text(fit(leftFields[i][1] || "-", leftW - 34, 10), margin + 10, ry + 24, 10, { font: fontBold });
    text(rightFields[i][0].toUpperCase(), margin + leftW + 12, ry + 12, 7, { font: fontBold, color: MUTED });
    text(fit(rightFields[i][1] || "-", rightW - 24, 10), margin + leftW + 12, ry + 24, 10, { font: fontBold });
  }
  y += gridH + 16;

  // --- Contract value strip (3 cells) ---
  const stripH = 46;
  const cellW = contentW / 3;
  const strip: [string, number, boolean][] = [
    ["ORIGINAL CONTRACT VALUE", data.originalContractValue, false],
    ["APPROVED VARIATIONS", data.approvedVariations, false],
    ["REVISED CONTRACT VALUE", data.revisedContractValue, true],
  ];
  strip.forEach(([label, value, emphasise], i) => {
    const cx = margin + i * cellW;
    rect(cx, y, cellW, stripH, emphasise ? LIGHT : LIGHT_ALT, { borderColor: BORDER, borderWidth: 1 });
    text(label, cx + cellW / 2, y + 16, 7, { font: fontBold, color: MUTED, align: "center" });
    text(formatCurrencyPage1(value), cx + cellW / 2, y + 36, 13, {
      font: fontBold,
      align: "center",
      color: emphasise ? NAVY : TEXT,
    });
  });
  y += stripH + 18;

  // --- Payment claim summary table ---
  // Rows 6/7/8 (Schedule 1/3/2) are informational references to the
  // schedules, NOT addends of C. Never assert C == P + V (false when priors
  // are unpaid). Row list lives in summaryRows.ts (mirror of the edge
  // function's summary-rows.ts, parity-tested) so this regeneration path and
  // the Send Report path cannot drift apart - see that file's header for the
  // layout history (Amendment 2 split, Josh's 27 Jul ceiling fix, Sep 2026
  // relayout). hasSplit false only for reports frozen before the Amendment 2
  // split (never derive the split from a frozen B).
  const rows: SummaryRow[] = buildPaymentSummaryRows({
    A: data.A,
    bReceipts: data.bReceipts ?? null,
    retentionHeldToDate: data.retentionHeldToDate ?? null,
    B: data.B,
    C: data.C,
    P: data.P,
    installHire: data.installHire,
    V: data.V,
    R: data.R,
    release: data.release,
    G: data.G,
    T: data.T,
    accumulatedRetention: data.accumulatedRetention ?? null,
    retentionCeiling: data.retentionCeiling ?? null,
    retentionCeilingPercent: data.retentionCeilingPercent ?? null,
    formatCurrency: formatCurrencyPage1,
  });

  const tableTop = y;
  rect(margin, y, contentW, 22, NAVY);
  text("PAYMENT CLAIM SUMMARY", margin + 10, y + 15, 10, { font: fontBold, color: WHITE });
  let ry = y + 22;
  const rowH = 21;
  rows.forEach((r, idx) => {
    if (r.total) rect(margin, ry, contentW, rowH, LIGHT);
    else if (r.bold) rect(margin, ry, contentW, rowH, rgb(0.955, 0.97, 0.99));
    else if (idx % 2 === 1) rect(margin, ry, contentW, rowH, LIGHT_ALT);
    const f = r.bold || r.total ? fontBold : font;
    // indent (not muted) drives the label x-position: Funds Receipted /
    // Retention Previously Held are muted grey but sit flush left like the
    // main rows, while the Schedule rows and the ceiling row are indented.
    const labelX = r.indent ? margin + 24 : margin + 10;
    text(r.label, labelX, ry + 14, r.total ? 11 : 9.2, { font: f, color: r.muted ? MUTED : TEXT });
    // Long tags ("D = C - H + Release", 20 chars) overlap the amount column
    // at the standard offset (measured: 67.5pt wide @ 7.5pt vs a ~53pt-wide
    // amount starting at right-118+that width) - push those further left.
    // Short tags ("A", "Schedule 1", "C = A - B") keep the original offset.
    if (r.tag) text(r.tag, right - (r.tag.length > 12 ? 150 : 118), ry + 14, 7.5, { color: MUTED });
    const amtColor = r.total ? NAVY : r.muted ? MUTED : TEXT;
    text(formatCurrencyPage1(r.amount), right - 10, ry + 14, r.total ? 14 : 9.5, {
      font: f,
      align: "right",
      color: amtColor,
    });
    line(margin, ry + rowH, margin + contentW, ry + rowH, 0.3, rgb(0.9, 0.92, 0.94));
    ry += rowH;
  });
  rect(margin, tableTop, contentW, ry - tableTop, undefined, { borderColor: BORDER, borderWidth: 1 });
  y = ry + 20;

  // --- Statutory text (BIF Act) ---
  text(
    "This is a progress claim made under the Building Industry Fairness (Security of Payment) Act 2017 (QLD)",
    margin,
    y + 10,
    9,
    { font: fontBold },
  );
  y += 26;

  // --- Bank details box ---
  const boxH = 82;
  rect(margin, y, contentW, boxH, LIGHT, { borderColor: BORDER, borderWidth: 0.75 });
  let by = y + 16;
  const heading = "Please pay all progress claims directly into our bank account";
  text(heading, margin + 12, by, 9, { font: fontBold, color: NAVY });
  line(margin + 12, by + 2, margin + 12 + fontBold.widthOfTextAtSize(heading, 9), by + 2, 0.5, NAVY);
  by += 18;
  const bankRow = (label: string, value: string) => {
    text(label, margin + 12, by, 9, { font: fontBold });
    text(value, margin + 70, by, 9, { font: fontBold });
    by += 12;
  };
  bankRow("Bank", data.bankName);
  bankRow("BSB", data.bsb);
  bankRow("Account", data.account);
  bankRow("Name", data.accountName);
}
